import Transform from '../math/Transform.js';
import AmbientOcclusion from '../ao/AmbientOcclusion.js';
import DOMUtils from '../utils/DOMUtils.js';

/**
 * Impostor - A screen-facing billboard sprite for static vegetation
 *
 * Creates a single div element positioned in screen space using worldToScreen projection.
 * Always faces the camera (billboard effect). Does not animate or move.
 *
 * Depth sorting: SAT-based, same as character sprites — sorted as virtual 1x1xscale
 * cuboids alongside regular cuboids. Water clipping uses WaterIntersectionManager
 * with multi-volume fragment support (same pattern as CharacterSprite).
 */
export default class Impostor {
  static _sessionPrefix = Math.random().toString(36).slice(2, 8);
  static _nextId = 1;

  /**
   * @param {Object} options
   * @param {string} [options.id] - Unique ID (auto-generated if omitted)
   * @param {number} options.x - Center X in game units
   * @param {number} options.y - Base Y (surface top Y — impostor sits ON this)
   * @param {number} options.z - Center Z in game units
   * @param {string} options.textureUrl - Path to sprite image (e.g. "textures/vegetation/...")
   * @param {number} [options.spriteWidth=64] - Natural pixel width of the texture
   * @param {number} [options.spriteHeight=64] - Natural pixel height of the texture
   * @param {number} [options.scale=1] - Display scale multiplier
   */
  constructor(options) {
    this.id = options.id || `imp_${Impostor._sessionPrefix}_${Impostor._nextId++}`;
    this.x = options.x;
    this.y = options.y;
    this.z = options.z;
    this.textureUrl = options.textureUrl;
    this.spriteWidth = options.spriteWidth || 64;
    this.spriteHeight = options.spriteHeight || 64;
    this.scale = options.scale || 1;
    this.flipped = options.flipped || false;

    // World reference (set by World.addImpostor)
    this.world = null;

    // AO brightness (sampled once on placement, re-sampled on AO rebuild)
    this._aoBrightness = 1;

    // Virtual footprint for depth sorting
    this.sortWidth = 1;
    this.sortDepth = 1;
    // Optional sort-center override: when set, the SAT bounding box is centered here
    // instead of at (this.x, this.z). Lets the visual position differ from the sort position.
    this.sortCenterX = null;
    this.sortCenterZ = null;

    // Water clipping state (multi-volume fragments, like CharacterSprite)
    this._isInWater = false;
    this._submergedFragments = new Map(); // volumeId → { element, clipInfo }
    this._belowWaterElement = null; // Fragment for below-water portion (behind floating water)
    this._belowWaterClip = null;

    // Create DOM element
    this.element = this._createElement();
  }

  /**
   * Create the DOM element for the impostor sprite
   */
  _createElement() {
    const el = document.createElement('div');
    el.className = 'impostor-sprite';
    Object.assign(el.style, {
      position: 'absolute',
      pointerEvents: 'none',
      imageRendering: 'pixelated',
      backgroundImage: `url('${this.textureUrl}')`,
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center bottom',
      transform: this.flipped ? 'scaleX(-1)' : '',
      display: 'none'
    });
    return el;
  }

  /**
   * Update screen position. Called each render frame for visible impostors.
   * @param {Object} camera - Camera object
   */
  update(camera) {
    const isAndroid = DOMUtils.isAndroid();
    const zoom = isAndroid ? camera.zoomLevel : 1;
    const effectiveCubeSize = camera.CUBE_SIZE * zoom;

    // Convert game units to world pixel coordinates
    const worldX = this.x * effectiveCubeSize;
    const worldY = this.y * effectiveCubeSize;
    const worldZ = -this.z * effectiveCubeSize;

    const screenPos = Transform.worldToScreen(
      worldX, worldY, worldZ,
      camera,
      isAndroid ? zoom : 1
    );

    // Scale: 1 game unit of height maps to effectiveCubeSize pixels
    // The sprite's display height = scale * effectiveCubeSize (so scale=1 means 1 cuboid tall)
    const displayHeight = this.scale * effectiveCubeSize;
    const aspectRatio = this.spriteWidth / this.spriteHeight;
    const displayWidth = displayHeight * aspectRatio;

    // Position: centered horizontally, bottom at surface level
    // Add effectiveCubeSize/2 to align with cuboid rendering (Cuboid.update has a cubeSize/2 offset
    // in effectiveCenterY that worldToScreen doesn't account for)
    const finalLeft = screenPos.x - displayWidth / 2;
    const finalTop = screenPos.y - displayHeight + effectiveCubeSize / 2;

    Object.assign(this.element.style, {
      width: `${displayWidth}px`,
      height: `${displayHeight}px`,
      left: `${finalLeft}px`,
      top: `${finalTop}px`,
      filter: this._aoBrightness < 1 ? `brightness(${this._aoBrightness})` : ''
    });
  }

  /**
   * Sort height for SAT depth-sorting. Defaults to the visual scale.
   * Set `_sortHeight` to override independently of the rendered sprite size
   * (e.g. projectiles use _sortHeight=2 to span the full wall height like
   * character sprites, while the visual fireball remains scale=0.3).
   */
  get sortHeight() {
    return this._sortHeight !== undefined ? this._sortHeight : this.scale;
  }

  /**
   * Apply water clipping for multiple water volumes.
   * Creates/reuses submerged fragment elements for each volume.
   * @param {string} aboveWaterClip - CSS clip-path for above-water portion
   * @param {Object[]} volumeClipInfos - Array of clip info objects (one per volume)
   */
  applyMultiVolumeWaterClip(aboveWaterClip, volumeClipInfos) {
    this._isInWater = true;
    const activeIds = new Set();

    for (const clipInfo of volumeClipInfos) {
      const volumeId = clipInfo.volumeId;
      activeIds.add(volumeId);

      // Get or create fragment for this volume
      let frag = this._submergedFragments.get(volumeId);
      if (!frag) {
        const fragEl = this._createElement();
        fragEl.className = 'impostor-sprite-submerged';
        frag = { element: fragEl, clipInfo: null };
        this._submergedFragments.set(volumeId, frag);
      }

      // Update fragment
      frag.clipInfo = clipInfo;
      frag.element.style.clipPath = this._adjustClipForFlip(clipInfo.submergedClipPath);
      this._syncFragmentElement(frag.element);
    }

    // Remove stale fragments (volumes impostor is no longer in)
    for (const [id, frag] of this._submergedFragments) {
      if (!activeIds.has(id)) {
        if (frag.element.parentNode) {
          frag.element.parentNode.removeChild(frag.element);
        }
        this._submergedFragments.delete(id);
      }
    }

    // Apply above-water clip to main element
    this.element.style.clipPath = this._adjustClipForFlip(aboveWaterClip);
  }

  /**
   * Get all submerged fragments (for depth sorting in World.js).
   * @returns {Object[]} Array of { element, clipInfo } objects
   */
  getSubmergedFragments() {
    return Array.from(this._submergedFragments.values());
  }

  /**
   * Remove water clipping — restores full sprite visibility.
   */
  clearWaterClip() {
    if (!this._isInWater) return;
    this._isInWater = false;
    this.element.style.clipPath = '';

    for (const [id, frag] of this._submergedFragments) {
      if (frag.element.parentNode) {
        frag.element.parentNode.removeChild(frag.element);
      }
    }
    this._submergedFragments.clear();
    this.clearBelowWaterFragment();
  }

  /**
   * Get or create the below-water fragment element (for flying water sorting).
   */
  getBelowWaterElement() {
    if (!this._belowWaterElement) {
      this._belowWaterElement = this._createElement();
      this._belowWaterElement.className = 'impostor-sprite-below-water';
    }
    return this._belowWaterElement;
  }

  /**
   * Sync below-water fragment with main element's position/appearance and apply clip.
   */
  syncBelowWaterFragment(clipPath) {
    const el = this.getBelowWaterElement();
    this._belowWaterClip = clipPath;
    el.style.clipPath = this._adjustClipForFlip(clipPath);
    this._syncFragmentElement(el);
  }

  /**
   * Hide and detach the below-water fragment.
   */
  clearBelowWaterFragment() {
    this._belowWaterClip = null;
    if (this._belowWaterElement) {
      this._belowWaterElement.style.display = 'none';
      if (this._belowWaterElement.parentNode) {
        this._belowWaterElement.parentNode.removeChild(this._belowWaterElement);
      }
    }
  }

  /**
   * Sync a fragment element with main element's position/appearance.
   * @param {HTMLElement} fragElement
   */
  _syncFragmentElement(fragElement) {
    const m = this.element;
    fragElement.style.display = '';
    fragElement.style.width = m.style.width;
    fragElement.style.height = m.style.height;
    fragElement.style.left = m.style.left;
    fragElement.style.top = m.style.top;
    fragElement.style.backgroundImage = m.style.backgroundImage;
    fragElement.style.filter = m.style.filter;
    fragElement.style.transform = m.style.transform;
  }

  /**
   * Mirror X coordinates in a CSS clip-path polygon for flipped impostors.
   * scaleX(-1) flips the rendered output but clip-path is applied before transform,
   * so we must pre-mirror the X coordinates to compensate.
   */
  _adjustClipForFlip(clipPath) {
    if (!this.flipped || !clipPath) return clipPath;
    return clipPath.replace(/polygon\(([^)]+)\)/, (_, coords) => {
      const mirrored = coords.split(',').map(pair => {
        const [xPart, yPart] = pair.trim().split(/\s+/);
        return `${(100 - parseFloat(xPart)).toFixed(2)}% ${yPart}`;
      }).join(', ');
      return `polygon(${mirrored})`;
    });
  }

  /**
   * Calculate depth for post-sort insertion using the SAT tiebreaker formula.
   * Uses a virtual 1x1 footprint centered at (x, z).
   * @param {number} sinAngle - sin of camera angle
   * @param {number} cosAngle - cos of camera angle
   * @param {boolean} viewingFromBelow - Whether camera is below the scene
   * @returns {number} Depth value (larger = closer to camera)
   */
  getSortDepth(sinAngle, cosAngle, viewingFromBelow) {
    const halfW = this.sortWidth / 2;
    const halfD = this.sortDepth / 2;
    const sx = this.sortCenterX !== null ? this.sortCenterX : this.x;
    const sz = this.sortCenterZ !== null ? this.sortCenterZ : this.z;

    const minX = sx - halfW;
    const maxX = sx + halfW;
    const minZ = sz - halfD;
    const maxZ = sz + halfD;
    const minY = this._sortMinY !== undefined ? this._sortMinY : this.y;
    const maxY = minY + this.sortHeight;

    const farX = sinAngle > 0 ? minX : maxX;
    const farZ = cosAngle > 0 ? maxZ : minZ;
    const farY = viewingFromBelow ? maxY : minY;

    return -farX * sinAngle + farZ * cosAngle + farY * 0.001 * (viewingFromBelow ? 1 : -1);
  }

  /**
   * Calculate screen-space bounding box for frustum culling.
   * @param {number} cosAngle
   * @param {number} sinAngle
   * @param {number} yScale
   * @param {number} cubeSize
   * @returns {{ minX: number, maxX: number, minY: number, maxY: number }}
   */
  getScreenBounds(cosAngle, sinAngle, yScale, cubeSize) {
    const halfW = this.sortWidth / 2;
    const halfD = this.sortDepth / 2;
    const sx = this.sortCenterX !== null ? this.sortCenterX : this.x;
    const sz = this.sortCenterZ !== null ? this.sortCenterZ : this.z;

    // 8 corners of the virtual sortWidth×sortDepth×sortHeight cuboid
    const corners = [
      { x: sx - halfW, y: this.y, z: sz - halfD },
      { x: sx + halfW, y: this.y, z: sz - halfD },
      { x: sx - halfW, y: this.y + this.sortHeight, z: sz - halfD },
      { x: sx + halfW, y: this.y + this.sortHeight, z: sz - halfD },
      { x: sx - halfW, y: this.y, z: sz + halfD },
      { x: sx + halfW, y: this.y, z: sz + halfD },
      { x: sx - halfW, y: this.y + this.sortHeight, z: sz + halfD },
      { x: sx + halfW, y: this.y + this.sortHeight, z: sz + halfD }
    ];

    let screenMinX = Infinity, screenMaxX = -Infinity;
    let screenMinY = Infinity, screenMaxY = -Infinity;

    for (const corner of corners) {
      const worldX = corner.x * cubeSize;
      const worldZ = -corner.z * cubeSize;
      const worldY = corner.y * cubeSize;

      const screenX = worldX * cosAngle - worldZ * sinAngle;
      const screenY = (worldX * sinAngle + worldZ * cosAngle) * yScale - worldY;

      if (screenX < screenMinX) screenMinX = screenX;
      if (screenX > screenMaxX) screenMaxX = screenX;
      if (screenY < screenMinY) screenMinY = screenY;
      if (screenY > screenMaxY) screenMaxY = screenY;
    }

    return { minX: screenMinX, maxX: screenMaxX, minY: screenMinY, maxY: screenMaxY };
  }

  /**
   * Sample AO brightness at impostor position (one-time, not per-frame).
   * Call on placement and when AO is rebuilt.
   */
  sampleAO() {
    if (!AmbientOcclusion.enabled || !this.world) {
      this._aoBrightness = 1;
      return;
    }

    // Find the floor cuboid at this position
    const cuboid = this._findFloorCuboid();
    if (!cuboid) {
      this._aoBrightness = 1;
      return;
    }

    this._aoBrightness = AmbientOcclusion.sampleAOAtWorldPoint(cuboid, 'top', this.x, this.z);
  }

  /**
   * Find the cuboid whose top face is at this impostor's Y position.
   * @returns {Cuboid|null}
   */
  _findFloorCuboid() {
    if (!this.world) return null;

    let bestCuboid = null;
    let bestDistance = Infinity;

    for (const cuboid of this.world.cuboids.values()) {
      const topY = cuboid.y + cuboid.height;

      // Check XZ overlap
      if (this.x < cuboid.x || this.x >= cuboid.x + cuboid.width) continue;
      if (this.z < cuboid.z || this.z >= cuboid.z + cuboid.depth) continue;

      // Distance from impostor base to cuboid top
      const distance = Math.abs(this.y - topY);
      if (distance < bestDistance) {
        bestCuboid = cuboid;
        bestDistance = distance;
      }
    }

    // Only accept cuboids very close to the impostor's base Y
    return bestDistance <= 0.5 ? bestCuboid : null;
  }

  /**
   * Attach the impostor's DOM element to a parent.
   * @param {HTMLElement} parent
   */
  attachTo(parent) {
    parent.appendChild(this.element);
  }

  /**
   * Remove from DOM.
   */
  detach() {
    if (this.element.parentElement) {
      this.element.parentElement.removeChild(this.element);
    }
  }

  /**
   * Toggle selection highlight on the impostor element.
   * @param {boolean} selected
   */
  setSelected(selected) {
    if (this.element) {
      this.element.style.outline = selected ? '2px solid #4a9eff' : '';
    }
  }

  /**
   * Clean up resources.
   */
  destroy() {
    this.clearWaterClip();
    this.detach();
    this.world = null;
    this.element = null;
  }

  /**
   * Serialize to JSON.
   */
  toJSON() {
    const data = {
      id: this.id,
      x: this.x,
      y: this.y,
      z: this.z,
      textureUrl: this.textureUrl,
      spriteWidth: this.spriteWidth,
      spriteHeight: this.spriteHeight,
      scale: this.scale
    };
    if (this.flipped) data.flipped = true;
    return data;
  }
}
